import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "./utils";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const secretaryApi = createApi({
  reducerPath: "secretaryApi",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: ["SecretaryPatients", "SecretaryDoctors", "SecretaryAppointments"],
  endpoints: (builder) => ({
    // Patients
    listPatients: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/patients/list", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    patientDistribution: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/patients/distribution", "POST"),
    }),
    createPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/patients/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    getUser: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/getUser", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updatePatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/patients/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    searchAssignablePatients: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/users/patients/assign-search", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    assignPatientToClinic: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/users/patients/assign-clinic", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),

    // Doctors in secretary clinic
    listClinicDoctors: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/clinic/doctors/list", "POST"),
      invalidatesTags: ["SecretaryDoctors"],
    }),

    // Appointments
    createAppointment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/create", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    listClinicAppointments: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/clinic-list", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    // Randevular › Genel: arama + durum + tarih aralığı, sayfalı
    searchAppointments: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/search", "POST"),
    }),
    listPatientAppointments: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/patient-list", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    cancelAppointment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/cancel", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    deleteAppointment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/delete", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    updateAppointmentLabTests: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/appointments/update-lab-tests", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    updateAppointmentDetails: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/appointments/update-details", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    updateAppointmentSchedule: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/appointments/update-schedule", "POST"),
      invalidatesTags: ["SecretaryAppointments"],
    }),
    // Treatments
    getTreatmentByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/get-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    listTreatmentsByPatient: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/treatments/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    createTreatment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updateTreatment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    setActiveTreatment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/set-active", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    setTreatmentStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/set-status", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    deleteTreatment: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/delete", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    listClinicPrescriptions: builder.mutation({
      query: (data) =>
        apiFunction(data, "/api/clinic/treatments/clinic-prescriptions", "POST"),
    }),
    // Klinik geneli listeler (Diyetler / Tedaviler sayfaları)
    listClinicDiets: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/clinic-diets", "POST"),
    }),
    // Diyet planları (2026-10-05) — tedaviden bağımsız kayıt
    listDietPlansByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/by-patient", "POST"),
    }),
    listClinicDietPlans: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/clinic", "POST"),
    }),
    getDietPlan: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/get", "POST"),
    }),
    createDietPlan: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/create", "POST"),
    }),
    updateDietPlan: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/update", "POST"),
    }),
    setDietPlanStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/set-status", "POST"),
    }),
    listNutritionIntakes: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/intakes", "POST"),
    }),
    saveNutritionIntake: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/intakes/save", "POST"),
    }),
    listPatientFoodLogs: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/food-logs", "POST"),
    }),
    getIntakeSummary: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/intake-summary", "POST"),
    }),
    commentFoodLog: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/diet-plans/food-logs/comment", "POST"),
    }),
    createManualMeasurement: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/measurements/create-manual", "POST"),
    }),
    listClinicTreatments: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/treatments/clinic-treatments", "POST"),
    }),
    // Anamnez
    listAnamnezByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/anamnez/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    createAnamnez: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/anamnez/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updateAnamnez: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/anamnez/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    deleteAnamnez: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/anamnez/delete", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    // Tetkik (hasta tetkikleri: laboratuvar + görüntüleme)
    listTetkikByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/tetkik/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    createTetkik: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/tetkik/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updateTetkik: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/tetkik/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    deleteTetkik: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/tetkik/delete", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    listClinicTetkik: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/tetkik/clinic-list", "POST"),
    }),
    // Ölçümler (cihaz verileri)
    listPatientMeasurements: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/measurements/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    // Bağlı Cihazlar — hastaya bağlı ortak cihaz kayıtları
    // Telefon Görüşmeleri
    listPhoneCallsByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/phone-calls/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    // Klinik geneli görüşmeler (Telefon Görüşmeleri sayfası)
    listClinicPhoneCalls: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/phone-calls/list-by-clinic", "POST"),
    }),
    createPhoneCall: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/phone-calls/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updatePhoneCall: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/phone-calls/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    deletePhoneCall: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/phone-calls/delete", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    listNotificationSettingsByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/list-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    createNotificationSetting: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/create", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    updateNotificationSetting: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/update", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    deleteNotificationSetting: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/delete", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    listNotificationLogsByPatient: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/list-logs-by-patient", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    sendNotificationNow: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/notification-settings/send-now", "POST"),
      invalidatesTags: ["SecretaryPatients"],
    }),
    uploadTetkikFile: builder.mutation({
      query: ({ token, tetkikId, patientId, clinicId, files }) => {
        const formData = new FormData();
        formData.append("tetkikId", tetkikId);
        formData.append("patientId", patientId);
        if (clinicId) formData.append("clinicId", clinicId);
        (files || []).forEach((file) => formData.append("files", file));
        return {
          url: "/api/clinic/tetkik/upload-file",
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      invalidatesTags: ["SecretaryPatients"],
    }),
  }),
});

export const {
  useListPatientsMutation,
  usePatientDistributionMutation,
  useCreatePatientMutation,
  useGetUserMutation,
  useUpdatePatientMutation,
  useSearchAssignablePatientsMutation,
  useAssignPatientToClinicMutation,
  useListClinicDoctorsMutation,
  useCreateAppointmentMutation,
  useListClinicAppointmentsMutation,
  useSearchAppointmentsMutation,
  useListPatientAppointmentsMutation,
  useCancelAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentLabTestsMutation,
  useUpdateAppointmentDetailsMutation,
  useUpdateAppointmentScheduleMutation,
  useGetTreatmentByPatientMutation,
  useListTreatmentsByPatientMutation,
  useCreateTreatmentMutation,
  useUpdateTreatmentMutation,
  useSetActiveTreatmentMutation,
  useSetTreatmentStatusMutation,
  useDeleteTreatmentMutation,
  useListAnamnezByPatientMutation,
  useCreateAnamnezMutation,
  useUpdateAnamnezMutation,
  useDeleteAnamnezMutation,
  useListTetkikByPatientMutation,
  useCreateTetkikMutation,
  useUpdateTetkikMutation,
  useDeleteTetkikMutation,
  useListClinicPrescriptionsMutation,
  useListClinicDietsMutation,
  useListDietPlansByPatientMutation,
  useListClinicDietPlansMutation,
  useGetDietPlanMutation,
  useCreateDietPlanMutation,
  useUpdateDietPlanMutation,
  useSetDietPlanStatusMutation,
  useListNutritionIntakesMutation,
  useSaveNutritionIntakeMutation,
  useCreateManualMeasurementMutation,
  useListPatientFoodLogsMutation,
  useCommentFoodLogMutation,
  useGetIntakeSummaryMutation,
  useListClinicTreatmentsMutation,
  useListClinicPhoneCallsMutation,
  useListClinicTetkikMutation,
  useListPhoneCallsByPatientMutation,
  useCreatePhoneCallMutation,
  useUpdatePhoneCallMutation,
  useDeletePhoneCallMutation,
  useListNotificationSettingsByPatientMutation,
  useCreateNotificationSettingMutation,
  useUpdateNotificationSettingMutation,
  useDeleteNotificationSettingMutation,
  useListNotificationLogsByPatientMutation,
  useSendNotificationNowMutation,
  useListPatientMeasurementsMutation,
  useUploadTetkikFileMutation,
} = secretaryApi;


